import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MenuIcon } from '@/components/icons/registry';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Icon } from '@/components/ui/icon';
import { useInstitutionRoles } from '@/compositions/hooks/useInstitutionRoles';
import { usePlatformAdmin } from '@/compositions/hooks/usePlatformAdmin';
import { MENU_GRID_ENABLED } from '@/config/features';
import {
  isDestinationRoute,
  isMenuRoute,
  getMenuItems,
  PLATFORM_MENU_ITEM,
} from '@/config/navigation';

import { AppMenuGrid } from './AppMenuGrid';
import { AppMenuList } from './AppMenuList';
import { BottomNavLabel, bottomNavItemClassName } from './BottomNavItem';

/**
 * Menu de módulos: a célula "Menu" da barra inferior e o painel que ela abre.
 *
 * ## Por que ele existe ao lado da barra inferior
 *
 * A barra tem cinco slots e eles estão decididos (`config/navigation.ts`). Todo
 * módulo novo — instituições hoje, doações e reencontro depois — precisaria
 * desalojar um deles para aparecer. O menu é onde os módulos cabem sem que a
 * barra vire uma disputa a cada entrega.
 *
 * ## Por que mora na barra, e o painel sobe do rodapé
 *
 * Até 2026-10 o gatilho era o hambúrguer no topo das telas; a planilha trocou
 * menu e perfil de lugar, e o gatilho desceu para a última célula da barra.
 * Gesto e leitura agora ficam na mesma zona: quem abre, lê e escolhe é o
 * polegar, que não alcança o topo de um telefone (docs/07). Uma gaveta lateral
 * colocaria a lista inteira justamente na área mais difícil de tocar. O
 * `BottomSheet` do catálogo já resolve arrasto para fechar, `Esc`, foco preso e
 * `safe-area`; uma gaveta nova refaria os quatro.
 *
 * ## Botão e painel juntos no mesmo componente
 *
 * O consumidor é só a `BottomNav`, que monta `<AppMenuTab />` na última célula.
 * Separar o gatilho do painel obrigaria a barra a carregar o estado de
 * aberto/fechado, que é assunto do menu e de mais ninguém.
 *
 * A célula acende como uma aba quando a tela atual veio do menu
 * (`isMenuRoute`) — sem isso, quem abria Doações via a barra inteira apagada.
 *
 * ## Duas formas do mesmo menu, por ora
 *
 * O corpo do painel sai daqui: `AppMenuGrid` (grade de cards) ou `AppMenuList`
 * (linhas com descrição), escolhidos por `MENU_GRID_ENABLED`. Aqui ficam o
 * gatilho, o painel e as duas decisões que valem para os dois corpos — o
 * `replace` do histórico e quem enxerga a porta da plataforma —, para que
 * apagar o perdedor quando a decisão fechar seja apagar **um arquivo**, sem
 * levar regra junto.
 *
 * O painel não tem descrição: "Módulos do Arcus" repetia o título com outras
 * palavras e empurrava a primeira porta para baixo, dentro de uma sobreposição
 * onde a altura é o recurso escasso.
 */
export function AppMenuTab() {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  // Trocar de tela-destino substitui a entrada de histórico em vez de empilhar
  // (`config/navigation.ts`): sem isso, cada toque no menu deixava um degrau na
  // pilha e o "voltar" percorria todas as telas visitadas, uma a uma. A origem
  // manda: saindo de uma tela de detalhe, o menu ainda empilha — a pessoa
  // precisa poder voltar para o que estava vendo.

  // A fila da plataforma é o único item condicional do menu: ela existe para
  // quem `is_platform_admin()` reconhece e para mais ninguém. Enquanto a
  // resposta não chega, o item **não** aparece — é melhor ele surgir um instante
  // depois do que piscar para quem nunca deveria vê-lo.
  const { isPlatformAdmin } = usePlatformAdmin();
  const roles = useInstitutionRoles();
  const shouldReplace = isDestinationRoute(pathname, roles);
  const isActive = isMenuRoute(pathname, roles);
  const menuItems = getMenuItems(roles);
  const items = isPlatformAdmin ? [...menuItems, PLATFORM_MENU_ITEM] : menuItems;

  const close = () => setIsOpen(false);

  return (
    <>
      <button
        type="button"
        // O rótulo visível "Menu" já é o nome acessível; `aria-haspopup` e
        // `aria-expanded` contam o resto ("Menu, botão, recolhido"). Um
        // `aria-label` diferente do texto à vista confundiria quem usa
        // comando de voz ("tocar Menu").
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(true)}
        className={bottomNavItemClassName({ isActive: isActive || isOpen })}
      >
        <Icon as={MenuIcon} size="lg" />
        <BottomNavLabel isStrong={isActive || isOpen}>Menu</BottomNavLabel>
      </button>

      <BottomSheet open={isOpen} onOpenChange={setIsOpen} title="Menu">
        <nav aria-label="Módulos">
          {MENU_GRID_ENABLED ? (
            <AppMenuGrid items={items} replace={shouldReplace} onSelect={close} />
          ) : (
            <AppMenuList items={items} replace={shouldReplace} onSelect={close} />
          )}
        </nav>
      </BottomSheet>
    </>
  );
}
